import type { Entry } from '@b2ag/contentful'

import type { CarouselContent } from '../../../domain/CarouselContent'
import type { CarouselContentDTO } from '../../../domain/CarouselContentDTO'
import type { ContentMedia } from '../../../../content-media/domain/ContentMedia'
import { ContentModelId } from '../../../../../types/ContentModelId'
import type { ContentPage } from '../../../../content-page/domain/ContentPage'
import type { ContentPageDTO } from '../../../../content-page/domain/ContentPageDTO'
import type { ListItems } from '../../../../list-items/domain/ListItems'
import type { Newsletter } from '../../../../newsletter/domain/Newsletter'
import type { PartnerId } from '../../../../../types/PartnerId'
import type { RichLink } from '../../../../rich-link/domain/RichLink'
import type { RichLinkDTO } from '../../../../rich-link/domain/RichLinkDTO'

import { isAvailablePartner } from '../../../../../utils/filter-by-partner'
import { isNonNullableEntryDTO } from '../../../../../utils/non-nullable-entry-dto'
import { mapEntryDTOList } from '../../../../../utils/map-entry-dto-list'

import { mapContentPageFromApi } from '../../../../content-page/infrastructure/repository/mapper/content-page.mapper'
import { mapRichLinkFromApi } from '../../../../rich-link/infrastructure/repository/mapper/rich-link.mapper'

const isContentPage = (dto: NonNullable<Entry>): dto is NonNullable<ContentPageDTO> =>
  dto.sys.contentType.sys.id === ContentModelId.CONTENT_PAGE
const isRichLink = (dto: NonNullable<Entry>): dto is NonNullable<RichLinkDTO> =>
  dto.sys.contentType.sys.id === ContentModelId.RICH_LINK

export const mapCarouselContentFromApi = (
  dto: CarouselContentDTO,
  partnerId?: PartnerId,
): CarouselContent | undefined => {
  if (!(isNonNullableEntryDTO(dto) && isAvailablePartner(partnerId, dto.fields.partnerFilter))) {
    return undefined
  }

  const {
    anchorLabel,
    backgroundColor,
    blocs,
    displayItemsMax,
    footer: footerDto,
    heading,
    tags,
    title,
    variants,
    withSeparator,
  } = dto.fields

  const blocks = mapEntryDTOList<ContentMedia | ContentPage | ListItems | Newsletter | RichLink>(blocs)
  let footer: undefined | ContentPage | RichLink

  if (isNonNullableEntryDTO(footerDto)) {
    if (isContentPage(footerDto)) {
      footer = mapContentPageFromApi(footerDto)
    } else if (isRichLink(footerDto)) {
      footer = mapRichLinkFromApi(footerDto)
    }
  }

  return {
    anchorLabel,
    blocs: blocks,
    contentType: ContentModelId.CAROUSEL_CONTENT,
    displayItemsMax,
    footer,
    heading,
    skinVariant: backgroundColor?.replace('-color', '') as any,
    tags,
    title,
    variants,
    withSeparator,
  }
}
